import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Link, useNavigate } from 'react-router-dom'
import { Form, Formik } from 'formik'
import {
  VerifyCodeFormValues,
  verifyCodeInitialValues,
  verifyCodeValidationSchema,
} from '@/pages/Verify/validation'
import { verifyCode } from '@/redux/asyncThunks/user'
import { fetchUserDataFromAPI } from '@/api/auth'
import { updateUser } from '@/redux/slices/userSlice'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/redux/store'
import ROUTES from '@/routes/routes'

const VerifyCode: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const handleSubmit = async (values: VerifyCodeFormValues) => {
    const formData = {
      otp: values.verifyCode,
    }

    const resultAction = await dispatch(verifyCode(formData.otp))

    if (verifyCode.fulfilled.match(resultAction)) {
      const user = await fetchUserDataFromAPI()
      dispatch(updateUser(user))
      navigate(ROUTES.PROFESSOR.ROOMS)
    }
  }

  return (
    <div className='flex h-screen w-screen flex-col md:flex-row'>
      <div className='relative hidden flex-1 md:block'>
        <img
          src='/images/auth-background.svg'
          alt='Auth background'
          className='absolute w-1/2 object-cover md:h-full md:w-5/6'
        />
        <div className='flex h-full items-center justify-center'>
          <img
            src='/images/auth-avatar.svg'
            alt='Auth Avatar'
            className='absolute z-10 object-contain md:h-full md:w-auto'
          />
        </div>
      </div>
      <div className='flex flex-1 flex-col items-center justify-center'>
        <div className='flex justify-center'>
          <img
            src='/images/oge.svg'
            alt='Huble logo'
            className='w-1/2 object-contain md:w-full'
          />
        </div>
        <div className='flex w-3/4 flex-col items-center md:w-full'>
          <Card className='w-full max-w-md border-none shadow-none'>
            <CardHeader className='space-y-3 flex flex-col items-center'>
              <CardTitle className='text-4xl font-bold leading-[50px]'>
                Welcome to OGE
              </CardTitle>
              <CardTitle className='text-xl text-[#023572] font-bold'>
                Verify Code
              </CardTitle>
              <CardDescription className='text-base text-black font-normal'>
                An authentication code has been sent to your email.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Formik
                initialValues={verifyCodeInitialValues}
                validationSchema={verifyCodeValidationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  isSubmitting,
                }) => (
                  <Form className='space-y-4'>
                    <div className='space-y-2'>
                      <Label
                        className={`text-base font-normal ${
                          touched.verifyCode && errors.verifyCode
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                        htmlFor='verifyCode'
                      >
                        Enter Code
                      </Label>
                      <Input
                        className={`h-10 placeholder:text-base placeholder:text-[#99A2B0] bg-[#FAFCFF] ${
                          touched.verifyCode && errors.verifyCode
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                        id='verifyCode'
                        name='verifyCode'
                        type='text'
                        placeholder='Enter Verify Code'
                        value={values.verifyCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.verifyCode && errors.verifyCode && (
                        <div className='text-red-500 text-sm flex gap-2'>
                          <img src='/icons/alert-circle.svg' alt='Warning' />
                          {errors.verifyCode}
                        </div>
                      )}
                    </div>
                    <div className='text-right'>
                      <span className='text-blacks text-base'>
                        Didn't receive a code?{' '}
                      </span>
                      <Link
                        to='#'
                        className='text-base text-dark-blue hover:text-blue-800'
                      >
                        Resend
                      </Link>
                    </div>
                    <Button
                      type='submit'
                      disabled={isSubmitting}
                      className='w-full h-[38px] text-base font-normal bg-dark-blue hover:bg-dark-blue-hovered'
                    >
                      Verify
                    </Button>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default VerifyCode
